import {createAction, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {rootPath} from 'app/appRoutes';
import {AppThunk} from 'app/store';
import {endLoading, startLoading} from 'app/apiSlice';
import {getUploadedFiles, GetUploadedFilesParams, saveImage, uploadFiles} from 'api';
import {ImageReviewStatusEnum, UploadedFile} from '../../interfaces/models';
import {SaveImageDataDto} from '../../interfaces/dto';
import errorHandler from '../shared/errorHandler';

interface FileUploadState {
    pageCount: number,
    uploadedFiles: UploadedFile[]
}

const initialState: FileUploadState = {
    pageCount: 0,
    uploadedFiles: []
};

const locationChangeAction = createAction<{location: {pathname: string}}>('@@router/LOCATION_CHANGE');

const fileUploadSlice = createSlice({
    name: 'fileUpload',
    initialState,
    reducers: {
        setState: (state: FileUploadState, action: PayloadAction<FileUploadState>) => {
            state.pageCount = action.payload.pageCount;
            state.uploadedFiles = action.payload.uploadedFiles;
        },
        updateImage: (state: FileUploadState, action: PayloadAction<SaveImageDataDto>) => {
            const {uploadedFiles} = state;
            const {payload: {image}} = action;
            const idx = uploadedFiles.findIndex(x => x.id === +image.imageId);
            if (idx < 0) return;
            const img = uploadedFiles[idx].imageData;
            if (!img) return;
            img.focalPointX = image.focalPointX;
            img.focalPointY = image.focalPointY;
            img.focalPointZ = image.focalPointZ;
            img.reviewStatus = ImageReviewStatusEnum.ReviewedManual;
        }
    },
    extraReducers: builder => {
        builder.addCase(locationChangeAction, (state, action) => {
            if (action.payload.location.pathname === rootPath) {
                return initialState;
            }
        })
    }
});

export default fileUploadSlice.reducer;

export const fetchUploadedFiles = (params: GetUploadedFilesParams, silent: boolean = false): AppThunk<void> => async (dispatch, getState) => {
    const token = getState().oidc.user?.access_token;
    if (!token) return;
    dispatch(startLoading('fetchUploadedFiles', silent));
    try {
        const response = await getUploadedFiles(token, params);
        dispatch(fileUploadSlice.actions.setState({pageCount: response.pageCount, uploadedFiles: response.data}));
    } catch (err) {
        errorHandler('fetchUploadedFiles error', err);
    }
    dispatch(endLoading('fetchUploadedFiles', silent));
}

export const saveFiles = (files: FileList): AppThunk<void> => async (dispatch, getState) => {
    const token = getState().oidc.user?.access_token;
    if (!token) return;
    dispatch(startLoading('uploadFiles'));
    try {
        const response = await uploadFiles(token, files);
        if (response.success) {
            // todo
        }
    } catch (err) {
        errorHandler('uploadFiles error', err);
    }
    dispatch(endLoading('uploadFiles'));
};

export const saveUploadedImageData = (data: SaveImageDataDto): AppThunk<void> => async (dispatch, getState) => {
    const token = getState().oidc.user?.access_token;
    if (!token) return;
    dispatch(startLoading('saveUploadedImageData'));
    try {
        const response = await saveImage(token, data);
        if (response.success)
            dispatch(fileUploadSlice.actions.updateImage(data));
    } catch (err) {
        errorHandler('saveImage error', err);
    }
    dispatch(endLoading('saveUploadedImageData'));
};